import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Section } from "@astryxdesign/core/Section";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Token } from "@astryxdesign/core/Token";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Table, pixel, proportional, type TableColumn } from "@astryxdesign/core/Table";
import { TextInput } from "@astryxdesign/core/TextInput";
import { AlertDialog } from "@astryxdesign/core/AlertDialog";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { useToast } from "@astryxdesign/core/Toast";
import { Plus, Pencil, Trash2, Lock, ShieldCheck, Search } from "lucide-react";
import { type CreateRoleInput, type Role, type UpdateRoleInput, api } from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { useIsNarrowViewport, useResponsiveTableDensity } from "../../lib/responsive";
import { RetryBanner } from "../../components/RetryBanner";
import { RoleFormDialog } from "./RoleFormDialog";

const LOADING_ROW_COUNT = 3;

interface RoleRow extends Record<string, unknown> {
  role: Role;
}

export function RolesPage() {
  const tableDensity = useResponsiveTableDensity();
  const isNarrow = useIsNarrowViewport();
  const queryClient = useQueryClient();
  const showToast = useToast();

  const {
    data: roles,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({ queryKey: ["roles"], queryFn: api.roles.list });
  const { data: catalog } = useQuery({ queryKey: ["permissions"], queryFn: api.permissions.list });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);
  const [search, setSearch] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["roles"] });

  const createMutation = useMutation({
    mutationFn: (input: CreateRoleInput) => api.roles.create(input),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Role added" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateRoleInput }) => api.roles.update(id, input),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Role updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.roles.delete(id),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Role removed" });
      setDeleteTarget(null);
    },
    onError: (err) => {
      showToast({ body: err instanceof Error ? err.message : "Couldn't delete role", type: "error" });
    },
  });

  const labelFor = (key: string) => catalog?.find((p) => p.key === key)?.label ?? key;
  const rows: RoleRow[] = (roles ?? []).map((role) => ({ role }));

  // Client-side, over data that's already fully loaded — see ProjectsPage's
  // own comment on why. Matches the role name and the (decoded) permission
  // labels, so searching "cameras" finds every role that can touch them
  // without needing to know the raw permission key.
  const searchLower = search.trim().toLowerCase();
  const filteredRows = searchLower
    ? rows.filter(
        ({ role }) =>
          role.name.toLowerCase().includes(searchLower) ||
          role.permissions.some((p) => labelFor(p).toLowerCase().includes(searchLower)),
      )
    : rows;

  const openAddDialog = () => {
    setEditing(null);
    setFormOpen(true);
  };

  // The permission cloud is the widest thing on this page — a dozen or more
  // wrapped tokens. As a column it squeezed the role name and the action
  // buttons on a phone, so at that width it moves under the name instead,
  // where it has the whole row to wrap into.
  const detailColumns: TableColumn<RoleRow>[] = [
    {
      key: "permissions",
      header: "Permissions",
      width: proportional(2),
      renderCell: ({ role }) => (
        <HStack gap={1} wrap="wrap">
          {role.permissions.map((p) => (
            <Token key={p} color="blue" label={labelFor(p)} size="sm" />
          ))}
        </HStack>
      ),
    },
  ];

  let body: ReactNode;
  if (isLoading) {
    body = (
      <VStack gap={2}>
        {Array.from({ length: LOADING_ROW_COUNT }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed-count static placeholders, never reordered
          <Skeleton key={i} height={44} radius={2} index={i} />
        ))}
      </VStack>
    );
  } else if (error) {
    body = (
      <RetryBanner
        title="Couldn't load roles"
        description={error instanceof Error ? error.message : "Unknown error"}
        onRetry={() => void refetch()}
        isRetrying={isRefetching}
      />
    );
  } else if (rows.length === 0) {
    // Was previously a bare Table with zero rows plus a hand-rolled block
    // stacked below it — the only page in the app whose empty state didn't
    // replace the table the way Cameras/Projects/Users do. EmptyState now
    // matches those three, including a real "Add role" call to action.
    body = (
      <EmptyState
        icon={<ShieldCheck size={32} />}
        title="No roles yet"
        description="Add a role to control what each user can see and do."
        actions={<Button label="Add role" variant="primary" icon={<Plus />} onClick={openAddDialog} />}
      />
    );
  } else {
    body = (
      <VStack gap={3}>
        <TextInput
          label="Search roles"
          isLabelHidden
          placeholder="Search by name or permission"
          value={search}
          onChange={setSearch}
          startIcon={<Search size={16} />}
          hasClear
        />
        {filteredRows.length === 0 ? (
          <EmptyState icon={<Search size={32} />} title="No matching roles" description={`No role matches "${search}".`} />
        ) : (
      <Table<RoleRow>
        data={filteredRows}
        idKey={(row) => row.role.id}
        density={tableDensity}
        hasHover
        dividers="rows"
        columns={[
          {
            key: "name",
            header: "Role",
            width: proportional(1),
            renderCell: ({ role }) => (
              <VStack gap={0}>
                <HStack gap={1.5} vAlign="center">
                  <Text weight="medium">{role.name}</Text>
                  {role.is_system && <Token label="Built-in" icon={<Lock size={12} />} size="sm" />}
                </HStack>
                {/* The tokens themselves, not a count. A count would be
                    tidier, but built-in roles have their edit button disabled,
                    so a phone viewer would have had no way anywhere in the app
                    to see what Admin or Viewer actually grants. Below the name
                    they get the full row width to wrap into, which is more
                    room than the column they were dropped from ever had. */}
                {isNarrow && (
                  <HStack gap={1} wrap="wrap">
                    {role.permissions.map((p) => (
                      <Token key={p} color="blue" label={labelFor(p)} size="sm" />
                    ))}
                  </HStack>
                )}
              </VStack>
            ),
          },
          ...(isNarrow ? [] : detailColumns),
          {
            key: "actions",
            header: "",
            width: pixel(96),
            renderCell: ({ role }) => (
              <HStack gap={1} justify="end">
                <IconButton
                  label={`Edit ${role.name}`}
                  tooltip={role.is_system ? "Built-in roles can't be edited" : "Edit"}
                  icon={<Pencil />}
                  variant="ghost"
                  size="sm"
                  isDisabled={role.is_system}
                  onClick={() => {
                    setEditing(role);
                    setFormOpen(true);
                  }}
                />
                <IconButton
                  label={`Delete ${role.name}`}
                  tooltip={role.is_system ? "Built-in roles can't be deleted" : "Delete"}
                  icon={<Trash2 />}
                  variant="ghost"
                  size="sm"
                  isDisabled={role.is_system}
                  onClick={() => setDeleteTarget(role)}
                />
              </HStack>
            ),
          },
        ]}
      />
        )}
      </VStack>
    );
  }

  return (
    <Section padding={4}>
      <VStack gap={4}>
        <PageHeader
          title="Roles"
          description={`${rows.length} role${rows.length === 1 ? "" : "s"} · assign one to each user to control what they can do`}
          actions={<Button label="Add role" variant="primary" icon={<Plus />} onClick={openAddDialog} />}
        />
        {body}
      </VStack>

      <RoleFormDialog
        isOpen={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        catalog={catalog ?? []}
        isSaving={createMutation.isPending || updateMutation.isPending}
        onCreate={(input) => createMutation.mutateAsync(input)}
        onUpdate={(id, input) => updateMutation.mutateAsync({ id, input })}
      />

      <AlertDialog
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.name ?? "this role"}?`}
        description="Accounts still assigned this role must be reassigned first. This cannot be undone."
        actionLabel="Delete role"
        isActionLoading={deleteMutation.isPending}
        onAction={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </Section>
  );
}
